import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllUsers,
  deleteUser,
  updateUserInfo,
  updateUserStatus,
} from "../store/slices/authSlice";
import { fetchSocieties } from "../store/slices/societySlice";
import Switcher3 from "../components/Switcher3";
import {
  Phone,
  Mail,
  UserPlus,
  Edit2,
  Trash2,
  Search,
  User,
  IdCard,
  Upload,
  Building,
  Users,
} from "lucide-react";
import { useSidebarContext } from "../scenes/global/sidebar/SidebarContext";

const InvestorTable = () => {
  const dispatch = useDispatch();
  const { users = [] } = useSelector((state) => state.auth);

  const { activeSociety, theme, gradient } = useSidebarContext();
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [updatedUsers, setUpdatedUsers] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);

  useEffect(() => {
    dispatch(fetchAllUsers());
    dispatch(fetchSocieties());
  }, [dispatch]);

  useEffect(() => {
    setUpdatedUsers(users);
  }, [users]);
  console.log(users);
  const filteredUsers = Array.isArray(updatedUsers)
    ? updatedUsers
        .filter((user) => user.role === "Investor")
        .filter((user) => {
          if (activeSociety && Array.isArray(user.societies)) {
            return user.societies.some((s) => s._id === activeSociety._id);
          }
          return true;
        })
        .filter((user) => {
          const username = user?.username || "";
          const name = user.investorData?.name || "";
          const investmentAmount = user.investorData?.investmentAmount || "";
          const profitPercentage = user.investorData?.profitPercentage || "";
          const email = user.investorData?.contactInfo?.email || "";
          const phone = user.investorData?.contactInfo?.phone || "";
          return (
            username.toLowerCase().includes(searchQuery.toLowerCase()) ||
            name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            email.toLowerCase().includes(searchQuery.toLowerCase()) ||
            phone.toLowerCase().includes(searchQuery.toLowerCase()) ||
            profitPercentage
              .toLowerCase()
              .includes(searchQuery.toLowerCase()) ||
            investmentAmount.toLowerCase().includes(searchQuery.toLowerCase())
          );
        })
    : [];

  const handleEdit = (user) => {
    setSelectedInvestor(user);
    setModalOpen(true);
  };

  const handleDelete = (userId) => {
    if (window.confirm("Are you sure you want to delete this Investor?")) {
      console.log("Deleting user with ID:", userId);
      dispatch(deleteUser(userId));
      setUpdatedUsers((prevUsers) =>
        prevUsers.filter((user) => user._id !== userId)
      );
      dispatch(fetchAllUsers());
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIsUploading(true);
      setSelectedInvestor((prev) => ({
        ...prev,
        profile_picture_file: file,
        profile_picture: URL.createObjectURL(file),
      }));
      setPreviewImage(URL.createObjectURL(file));
      setIsUploading(false);
    }
  };

  const handleInputChange = (e, field) => {
    const { value } = e.target;
    setSelectedInvestor((prevSelectedInvestor) => ({
      ...prevSelectedInvestor,
      investorData: {
        ...prevSelectedInvestor.investorData,
        contactInfo: {
          ...prevSelectedInvestor.investorData.contactInfo,
          [field]: value,
        },
      },
    }));
  };

  const cleanProfilePictureURL = (url) => {
    if (!url) return null;
    if (url.includes("//uploads")) {
      return url.replace("//uploads", "/uploads");
    }

    return url;
  };

  const handleUpdate = () => {
    if (!selectedInvestor?._id) {
      console.error("User ID is missing, cannot update.");
      return;
    }
    const formData = new FormData();

    formData.append("username", selectedInvestor.username);
    formData.append("role", "Investor");
    if (selectedInvestor.societies) {
      formData.append("societies", JSON.stringify(selectedInvestor.societies));
    }
    if (selectedInvestor.status) {
      formData.append("status", selectedInvestor.status);
    }

    if (selectedInvestor.profile_picture_file) {
      formData.append("profile_picture", selectedInvestor.profile_picture_file);
    }

    if (selectedInvestor.investorData) {
      formData.append(
        "investorData[name]",
        selectedInvestor.investorData.name || ""
      );

      if (selectedInvestor.investorData.contactInfo) {
        const contactInfo = selectedInvestor.investorData.contactInfo;

        formData.append(
          "investorData[contactInfo][phone]",
          contactInfo.phone || ""
        );
        formData.append(
          "investorData[contactInfo][email]",
          contactInfo.email || ""
        );
        formData.append(
          "investorData[contactInfo][cnic]",
          contactInfo.cnic || ""
        );
        formData.append(
          "investorData[contactInfo][address]",
          contactInfo.address || ""
        );
      }
    }

    console.log("Sending updated data as FormData");

    dispatch(
      updateUserInfo({ userId: selectedInvestor._id, updatedData: formData })
    )
      .then((res) => {
        console.log("Update response:", res);

        if (res.meta.requestStatus === "fulfilled") {
          console.log("Update successful, fetching users...");
          dispatch(fetchAllUsers()).then((res) => {
            setUpdatedUsers(res.payload);
          });
          setModalOpen(false);
        } else {
          console.error("Update failed:", res.error);
        }
      })
      .catch((error) => {
        console.error("Error updating user:", error);
      });
  };

  const handleStatusChange = (userId, newStatus) => {
    const updatedData = { status: newStatus };
    console.log("Sending status update:", { userId, updatedData });
    dispatch(updateUserStatus({ userId, status: newStatus }))
      .then((res) => {
        if (res.meta.requestStatus === "fulfilled") {
          dispatch(fetchAllUsers());
          console.log("Status updated successfully for user ID:", userId);
        } else {
          console.error("Status update failed:", res.error);
        }
      })
      .catch((error) => {
        console.error("Error updating user status:", error);
      });
  };

  function formatCNIC(cnic) {
    if (!cnic) return "";
    cnic = cnic.replace(/\D/g, "");
    cnic = cnic.slice(0, 13);
    return cnic.replace(/^(\d{5})(\d{7})(\d{1})$/, "$1-$2-$3");
  }

  return (
    <div
      className={`${gradient} rounded-2xl shadow-[0_8px_32px_0_rgba(0,0,0,0.36)] p-8 animate-gradient-x`}
      style={{
        backgroundImage: `
      linear-gradient(to bottom right, ${theme.mui.bg2}),
      url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100%25' height='100%25'%3E%3Crect fill='%231A1F2E' width='540' height='450'/%3E%3Cdefs%3E%3ClinearGradient id='a' gradientUnits='userSpaceOnUse' x1='0' x2='0' y1='0' y2='100%25' gradientTransform='rotate(240)'%3E%3Cstop offset='0' stop-color='%231A1F2E'/%3E%3Cstop offset='1' stop-color='%23373F5A'/%3E%3C/linearGradient%3E%3Cpattern patternUnits='userSpaceOnUse' id='b' width='300' height='250' x='0' y='0' viewBox='0 0 1080 900'%3E%3Cg fill-opacity='0.04'%3E%3Cpolygon fill='%23444' points='90 150 0 300 180 300'/%3E%3Cpolygon points='90 150 180 0 0 0'/%3E%3Cpolygon fill='%23AAA' points='270 150 360 0 180 0'/%3E%3Cpolygon fill='%23DDD' points='450 150 360 300 540 300'/%3E%3Cpolygon fill='%23999' points='450 150 540 0 360 0'/%3E%3Cpolygon points='630 150 540 300 720 300'/%3E%3Cpolygon fill='%23DDD' points='630 150 720 0 540 0'/%3E%3Cpolygon fill='%23444' points='810 150 720 300 900 300'/%3E%3Cpolygon fill='%23FFF' points='810 150 900 0 720 0'/%3E%3Cpolygon fill='%23DDD' points='990 150 900 300 1080 300'/%3E%3Cpolygon fill='%23444' points='990 150 1080 0 900 0'/%3E%3Cpolygon fill='%23DDD' points='90 450 0 600 180 600'/%3E%3Cpolygon points='90 450 180 300 0 300'/%3E%3Cpolygon fill='%23666' points='270 450 180 600 360 600'/%3E%3Cpolygon fill='%23AAA' points='270 450 360 300 180 300'/%3E%3Cpolygon fill='%23DDD' points='450 450 360 600 540 600'/%3E%3Cpolygon fill='%23999' points='450 450 540 300 360 300'/%3E%3Cpolygon fill='%23999' points='630 450 540 600 720 600'/%3E%3Cpolygon fill='%23FFF' points='630 450 720 300 540 300'/%3E%3Cpolygon points='810 450 720 600 900 600'/%3E%3Cpolygon fill='%23DDD' points='810 450 900 300 720 300'/%3E%3Cpolygon fill='%23AAA' points='990 450 900 600 1080 600'/%3E%3Cpolygon fill='%23444' points='990 450 1080 300 900 300'/%3E%3Cpolygon fill='%23222' points='90 750 0 900 180 900'/%3E%3Cpolygon points='270 750 180 900 360 900'/%3E%3Cpolygon fill='%23DDD' points='270 750 360 600 180 600'/%3E%3Cpolygon points='450 750 540 600 360 600'/%3E%3Cpolygon points='630 750 540 900 720 900'/%3E%3Cpolygon fill='%23444' points='630 750 720 600 540 600'/%3E%3Cpolygon fill='%23AAA' points='810 750 720 900 900 900'/%3E%3Cpolygon fill='%23666' points='810 750 900 600 720 600'/%3E%3Cpolygon fill='%23999' points='990 750 900 900 1080 900'/%3E%3Cpolygon fill='%23999' points='180 0 90 150 270 150'/%3E%3Cpolygon fill='%23444' points='360 0 270 150 450 150'/%3E%3Cpolygon fill='%23FFF' points='540 0 450 150 630 150'/%3E%3Cpolygon points='900 0 810 150 990 150'/%3E%3Cpolygon fill='%23222' points='0 300 -90 450 90 450'/%3E%3Cpolygon fill='%23FFF' points='0 300 90 150 -90 150'/%3E%3Cpolygon fill='%23FFF' points='180 300 90 450 270 450'/%3E%3Cpolygon fill='%23666' points='180 300 270 150 90 150'/%3E%3Cpolygon fill='%23222' points='360 300 270 450 450 450'/%3E%3Cpolygon fill='%23FFF' points='360 300 450 150 270 150'/%3E%3Cpolygon fill='%23444' points='540 300 450 450 630 450'/%3E%3Cpolygon fill='%23222' points='540 300 630 150 450 150'/%3E%3Cpolygon fill='%23AAA' points='720 300 630 450 810 450'/%3E%3Cpolygon fill='%23666' points='720 300 810 150 630 150'/%3E%3Cpolygon fill='%23FFF' points='900 300 810 450 990 450'/%3E%3Cpolygon fill='%23999' points='900 300 990 150 810 150'/%3E%3Cpolygon points='0 600 -90 750 90 750'/%3E%3Cpolygon fill='%23666' points='0 600 90 450 -90 450'/%3E%3Cpolygon fill='%23AAA' points='180 600 90 750 270 750'/%3E%3Cpolygon fill='%23444' points='180 600 270 450 90 450'/%3E%3Cpolygon fill='%23444' points='360 600 270 750 450 750'/%3E%3Cpolygon fill='%23999' points='360 600 450 450 270 450'/%3E%3Cpolygon fill='%23666' points='540 600 630 450 450 450'/%3E%3Cpolygon fill='%23222' points='720 600 630 750 810 750'/%3E%3Cpolygon fill='%23FFF' points='900 600 810 750 990 750'/%3E%3Cpolygon fill='%23222' points='900 600 990 450 810 450'/%3E%3Cpolygon fill='%23DDD' points='0 900 90 750 -90 750'/%3E%3Cpolygon fill='%23444' points='180 900 270 750 90 750'/%3E%3Cpolygon fill='%23FFF' points='360 900 450 750 270 750'/%3E%3Cpolygon fill='%23AAA' points='540 900 630 750 450 750'/%3E%3Cpolygon fill='%23FFF' points='720 900 810 750 630 750'/%3E%3Cpolygon fill='%23222' points='900 900 990 750 810 750'/%3E%3Cpolygon fill='%23222' points='1080 300 990 450 1170 450'/%3E%3Cpolygon fill='%23FFF' points='1080 300 1170 150 990 150'/%3E%3Cpolygon points='1080 600 990 750 1170 750'/%3E%3Cpolygon fill='%23666' points='1080 600 1170 450 990 450'/%3E%3Cpolygon fill='%23DDD' points='1080 900 1170 750 990 750'/%3E%3C/g%3E%3C/pattern%3E%3C/defs%3E%3Crect x='0' y='0' fill='url(%23a)' width='100%25' height='100%25'/%3E%3Crect x='0' y='0' fill='url(%23b)' width='100%25' height='100%25'/%3E%3C/svg%3E")
    `,
      }}
    >
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Enhanced Header Section */}
        <div className="relative group p-6 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 hover:border-white/20 transition-all duration-500">
          <div className="flex flex-col space-y-3">
            <h1
              className={`text-4xl font-bold tracking-tight ${theme.tailwind.bgr6} text-transparent bg-clip-text transform group-hover:scale-[1.02] transition-all duration-500`}
            >
              Investor Management
            </h1>
            <div
              className={`flex items-center space-x-2 ${theme.tailwind.text4}`}
            >
              <Building className="h-5 w-5" />
              <span>{activeSociety?.name || "No Society Selected"}</span>
            </div>
            <div className="flex items-center space-x-2 bg-white/5 px-4 py-2 rounded-xl">
              <Users className={`h-5 w-5 ${theme.tailwind.text9}`} />
              <span className={`${theme.tailwind.text9} font-medium`}>
                {filteredUsers.length} Investors
              </span>
            </div>
            <p
              className={`${theme.tailwind.text4} text-lg transform group-hover:translate-x-2 transition-all duration-500`}
            >
              Manage and monitor your investor base efficiently
            </p>
          </div>
          <div
            className={`absolute inset-0 rounded-2xl ${theme.tailwind.bgr20} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
          />
        </div>

        {/* Enhanced Search Bar */}
        <div className="relative group transform hover:-translate-y-1 transition-all duration-500">
          <div
            className={`absolute inset-0 rounded-xl ${theme.tailwind.bgr21} blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500`}
          />
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <Search
              className={`h-6 w-6 ${theme.tailwind.text9} group-hover:rotate-12 transition-transform duration-500`}
            />
          </div>
          <input
            type="text"
            placeholder="Search users by name, email, or CNIC..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`w-full pl-12 pr-4 py-4 rounded-xl bg-white/5 border border-white/10 ${theme.tailwind.text9} 
            ${theme.tailwind.placeHolder} focus:outline-none focus:ring-2 ${theme.tailwind.focusRing6} 
            ${theme.tailwind.focus7} transition-all duration-500 backdrop-blur-xl
            hover:bg-white/10 hover:shadow-lg ${theme.tailwind.shadowhover6}`}
          />
        </div>

        <div className="relative group">
          <div className="absolute inset-0 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500" />
          <div
            className={`relative overflow-hidden rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 
              hover:border-white/20 transition-all duration-500 hover:shadow-xl ${theme.tailwind.shadowhover6}`}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10 bg-white/5">
                    <th
                      className={`px-6 py-4 text-left ${theme.tailwind.text9} font-medium`}
                    >
                      User Details
                    </th>
                    <th
                      className={`px-6 py-4 text-left ${theme.tailwind.text9} font-medium`}
                    >
                      Society
                    </th>
                    <th
                      className={`px-6 py-4 text-left ${theme.tailwind.text9} font-medium`}
                    >
                      Contact Info
                    </th>
                    <th
                      className={`px-6 py-4 text-left ${theme.tailwind.text9} font-medium`}
                    >
                      Investment Amount
                    </th>
                    <th
                      className={`px-6 py-4 text-left ${theme.tailwind.text9} font-medium`}
                    >
                      profit %
                    </th>
                    <th
                      className={`px-6 py-4 text-left ${theme.tailwind.text9} font-medium`}
                    >
                      Status
                    </th>
                    <th
                      className={`px-6 py-4 text-left ${theme.tailwind.text9} font-medium`}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((user) => (
                      <tr
                        key={user._id}
                        onMouseEnter={() => setHoveredRow(user._id)}
                        onMouseLeave={() => setHoveredRow(null)}
                        className="group/row border-b border-white/5 hover:bg-white/10 transition-all duration-500 cursor-pointer"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-4">
                            <div className="relative group/avatar">
                              {user.profile_picture ? (
                                <img
                                  src={cleanProfilePictureURL(
                                    user.profile_picture
                                  )}
                                  alt={user.employeeData?.name}
                                  className={`w-12 h-12 rounded-full object-cover border-2 ${theme.tailwind.border1} 
                                                            group-hover/avatar:scale-110 transition-all duration-500`}
                                />
                              ) : (
                                <div
                                  className={`w-12 h-12 rounded-full ${theme.tailwind.bg52} flex items-center justify-center 
                                                            border-2 ${theme.tailwind.border1} group-hover/avatar:scale-110 transition-all duration-500`}
                                >
                                  <User
                                    className={`h-6 w-6 ${theme.tailwind.text9}`}
                                  />
                                </div>
                              )}
                              <div
                                className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full ${theme.tailwind.bg40} border-2 ${theme.tailwind.border28}
                                                          group-hover/avatar:scale-125 transition-all duration-500`}
                              />
                            </div>
                            <div>
                              <div
                                className={`${theme.tailwind.text9} font-medium group-hover/row:translate-x-2 transition-transform duration-500`}
                              >
                                {user.investorData?.name || "N/A"}
                              </div>
                              <div
                                className={`${theme.tailwind.text20} text-sm group-hover/row:translate-x-2 transition-transform duration-500 delay-75`}
                              >
                                {user.username}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className={`px-6 py-4 ${theme.tailwind.text9}`}>
                          {user.societies && Array.isArray(user.societies)
                            ? user.societies
                                .map((society) => society.name)
                                .join(", ")
                            : "N/A"}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col space-y-2">
                            <div
                              className={`flex items-center space-x-2 ${theme.tailwind.text9}`}
                            >
                              <Phone className="h-4 w-4" />
                              <span>
                                {user.investorData?.contactInfo?.phone || "N/A"}
                              </span>
                            </div>
                            <div
                              className={`flex items-center space-x-2 ${theme.tailwind.text20}`}
                            >
                              <Mail className="h-4 w-4" />
                              <span>
                                {user.investorData?.contactInfo?.email || "N/A"}
                              </span>
                            </div>
                            <div
                              className={`flex items-center space-x-2 ${theme.tailwind.text20}`}
                            >
                              <IdCard className="h-4 w-4" />
                              <span>
                                {user.investorData?.contactInfo?.address ||
                                  "N/A"}
                              </span>
                            </div>
                            <div
                              className={`flex items-center space-x-2 ${theme.tailwind.text20}`}
                            >
                              <IdCard className="h-4 w-4" />
                              <span>
                                {user.investorData?.contactInfo?.cnic
                                  ? formatCNIC(
                                      user.investorData.contactInfo.cnic
                                    )
                                  : "N/A"}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-3">
                          Rs.
                          {user.investorData?.investmentAmount
                            ? Number(
                                user.investorData?.investmentAmount
                              ).toLocaleString()
                            : "N/A"}
                        </td>
                        <td className="px-6 py-3">
                          {user.investorData?.profitPercentage || "N/A"}
                        </td>
                        <td className="px-6 py-4">
                          <Switcher3
                            isChecked={user.status === "active"}
                            onToggle={(newStatus) =>
                              handleStatusChange(
                                user._id,
                                newStatus ? "active" : "inactive"
                              )
                            }
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex space-x-3">
                            <button
                              onClick={() => handleEdit(user)}
                              className={`p-2 rounded-lg ${theme.tailwind.bg52} ${theme.tailwind.bgHover26} ${theme.tailwind.text9} 
                        transform hover:scale-110 transition-all duration-300`}
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(user._id)}
                              className="p-2 rounded-lg bg-red-400/10 hover:bg-red-400/20 text-red-400 
                        transform hover:scale-110 transition-all duration-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr className="animate-fade-in">
                      <td colSpan="7" className="py-10">
                        <div className="flex flex-col items-center justify-center space-y-4">
                          <div className="relative">
                            <div
                              className={`absolute -inset-1 rounded-full ${theme.tailwind.bg1} animate-pulse`}
                            ></div>
                            <div
                              className={`relative flex items-center justify-center w-16 h-16 rounded-full ${theme.tailwind.bgr7} shadow-lg`}
                            >
                              <svg
                                className="w-8 h-8 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                ></path>
                              </svg>
                            </div>
                          </div>
                          <p
                            className={`text-xl font-semibold ${theme.tailwind.bgr7} bg-clip-text text-transparent`}
                          >
                            No investors found
                          </p>
                          <p
                            className={`${theme.tailwind.text19} text-sm max-w-md text-center`}
                          >
                            Try adjusting your search criteria or add a new
                            investor to get started.
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {isModalOpen && selectedInvestor && (
          <div className="-mt-10">
            <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md flex items-center justify-center z-50">
              <div
                className={`relative group ${gradient} p-8 rounded-3xl 
                border border-white/10 shadow-2xl backdrop-blur-xl w-full max-w-2xl transform 
                transition-all duration-500 hover:scale-[1.02] hover:border-white/20`}
              >
                <div
                  className={`absolute inset-0 rounded-3xl ${theme.tailwind.bgr20} 
                  opacity-0 group-hover:opacity-100 transition-all duration-500`}
                />
                {/* Header */}
                <h2
                  className={`text-3xl font-bold mb-8 ${theme.tailwind.bgr4} 
                     text-transparent bg-clip-text text-center`}
                >
                  Edit Investor Info
                </h2>
                {/* Profile Image Section */}
                <div className="flex justify-center mb-8">
                  <div className="relative group">
                    <div
                      className={`w-32 h-32 rounded-full overflow-hidden border-4 ${theme.tailwind.border1} 
                          ${theme.tailwind.groupHover11} transition-all duration-300`}
                    >
                      <img
                        src={
                          previewImage ||
                          selectedInvestor.profile_picture ||
                          "/api/placeholder/128/128"
                        }
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                      <div
                        className="absolute inset-0 bg-black/50 flex items-center justify-center 
                            opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      >
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={handleImageChange}
                          />
                          <Upload
                            className={`h-8 w-8 ${theme.tailwind.text9}`}
                          />
                        </label>
                      </div>
                    </div>
                    {isUploading && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div
                          className={`animate-spin rounded-full h-8 w-8 border-b-2 ${theme.tailwind.border25}`}
                        ></div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-6">
                  {/* First Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User
                          className={`h-5 w-5 ${theme.tailwind.text2} ${theme.tailwind.groupHover10} transition-colors duration-300`}
                        />
                      </div>
                      <input
                        type="text"
                        placeholder="Investor Name"
                        value={selectedInvestor.investorData?.name || ""}
                        onChange={(e) =>
                          setSelectedInvestor((prev) => ({
                            ...prev,
                            investorData: {
                              ...prev.investorData,
                              name: e.target.value,
                            },
                          }))
                        }
                        className={`w-full pl-10 p-4 rounded-xl bg-white/5 border border-white/10 ${theme.tailwind.text9} 
                          ${theme.tailwind.placeHolder} focus:outline-none focus:ring-2 ${theme.tailwind.focusRing6} 
                          ${theme.tailwind.focus7} transition-all duration-300 hover:bg-white/10`}
                      />
                    </div>

                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <IdCard
                          className={`h-5 w-5 ${theme.tailwind.text2} ${theme.tailwind.groupHover10} transition-colors duration-300`}
                        />
                      </div>
                      <input
                        type="text"
                        placeholder="Address"
                        value={
                          selectedInvestor.investorData?.contactInfo?.address ||
                          ""
                        }
                        onChange={(e) => handleInputChange(e, "address")}
                        className={`w-full pl-10 p-4 rounded-xl bg-white/5 border border-white/10 ${theme.tailwind.text9} 
                          ${theme.tailwind.placeHolder} focus:outline-none focus:ring-2 ${theme.tailwind.focusRing6} 
                          ${theme.tailwind.focus7} transition-all duration-300 hover:bg-white/10`}
                      />
                    </div>
                  </div>

                  {/* Second Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Phone
                          className={`h-5 w-5 ${theme.tailwind.text2} ${theme.tailwind.groupHover10} transition-colors duration-300`}
                        />
                      </div>
                      <input
                        type="text"
                        placeholder="Phone"
                        value={
                          selectedInvestor.investorData?.contactInfo?.phone ||
                          ""
                        }
                        onChange={(e) => handleInputChange(e, "phone")}
                        className={`w-full pl-10 p-4 rounded-xl bg-white/5 border border-white/10 ${theme.tailwind.text9} 
                          ${theme.tailwind.placeHolder} focus:outline-none focus:ring-2 ${theme.tailwind.focusRing6} 
                          ${theme.tailwind.focus7} transition-all duration-300 hover:bg-white/10`}
                      />
                    </div>

                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail
                          className={`h-5 w-5 ${theme.tailwind.text2} ${theme.tailwind.groupHover10} transition-colors duration-300`}
                        />
                      </div>
                      <input
                        type="text"
                        placeholder="Email"
                        value={
                          selectedInvestor.investorData?.contactInfo?.email ||
                          ""
                        }
                        onChange={(e) => handleInputChange(e, "email")}
                        className={`w-full pl-10 p-4 rounded-xl bg-white/5 border border-white/10 ${theme.tailwind.text9} 
                          ${theme.tailwind.placeHolder} focus:outline-none focus:ring-2 ${theme.tailwind.focusRing6} 
                          ${theme.tailwind.focus7} transition-all duration-300 hover:bg-white/10`}
                      />
                    </div>
                  </div>

                  {/* Username Field */}
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <UserPlus
                        className={`h-5 w-5 ${theme.tailwind.text2} ${theme.tailwind.groupHover10} transition-colors duration-300`}
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Username"
                      value={selectedInvestor.username || ""}
                      onChange={(e) =>
                        setSelectedInvestor((prev) => ({
                          ...prev,
                          username: e.target.value,
                        }))
                      }
                      className={`w-full pl-10 p-4 rounded-xl bg-white/5 border border-white/10 ${theme.tailwind.text9} 
                        ${theme.tailwind.placeHolder} focus:outline-none focus:ring-2 ${theme.tailwind.focusRing6} 
                        ${theme.tailwind.focus7} transition-all duration-300 hover:bg-white/10`}
                    />
                  </div>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <IdCard
                        className={`h-5 w-5 ${theme.tailwind.text2} ${theme.tailwind.groupHover10} transition-colors duration-300`}
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="CNIC"
                      value={
                        selectedInvestor.investorData?.contactInfo?.cnic
                          ? formatCNIC(
                              selectedInvestor.investorData.contactInfo.cnic
                            )
                          : ""
                      }
                      onChange={(e) => handleInputChange(e, "cnic")}
                      className={`w-full pl-10 p-4 rounded-xl bg-white/5 border border-white/10 ${theme.tailwind.text9} 
                ${theme.tailwind.placeHolder} focus:outline-none focus:ring-2 ${theme.tailwind.focusRing6} 
                ${theme.tailwind.focus7} transition-all duration-300 hover:bg-white/10`}
                    />
                  </div>
                </div>
                {/* Action Buttons */}
                <div className="flex space-x-4 mt-8">
                  <button
                    onClick={handleUpdate}
                    className={`flex-1 ${theme.tailwind.bgr19} text-white px-6 py-3 rounded-xl 
                     font-semibold transform hover:scale-105 transition-all duration-300 
                     hover:shadow-lg ${theme.tailwind.shadowhover3} active:scale-95`}
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={() => setModalOpen(false)}
                    className="flex-1 bg-gradient-to-r from-red-400 to-red-500 text-white px-6 py-3 rounded-xl 
                     font-semibold transform hover:scale-105 transition-all duration-300 
                     hover:shadow-lg hover:shadow-red-500/25 active:scale-95"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvestorTable;
